import Fuse from 'fuse.js';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useMemo, useCallback } from 'react';

import debounce from '../../lib/debounce';
import IconChevronDown from '../Icons/IconChevronDown';
import IconChevronUp from '../Icons/IconChevronUp';

import './index.scss';

const filterOnChange = (term, options, disableFuzzyMatch, fuseConfigKeys) => {
  if (!term.length) return options;
  if (disableFuzzyMatch)
    return options.filter(opt => opt.label.toLowerCase().includes(term.trim().toLowerCase()));

  const fuseConfig = {
    threshold: 0.4,
    distance: 100,
    ignoreLocation: true,
    findAllMatches: true,
    includeScore: true,
    shouldSort: true,
    fieldNormWeight: 3,
    keys: fuseConfigKeys,
  };

  const fuse = new Fuse(options, fuseConfig);
  const results = fuse.search(term);
  return results.map(r => r.item);
};

const shouldDisableSubmit = (selectedValue, inputValue) => {
  return selectedValue !== inputValue;
};

const CustomSelect = ({
  initialValue,
  placeholder,
  options,
  onChange,
  noOptionsMessage,
  type,
  autoFocus,
  disabled,
  disableFuzzyMatch,
  fuseConfigKeys,
  getTerm,
  minCharSearchLength,
  label,
  displayError,
}) => {
  const [term, setTerm] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [focussed, setFocussed] = useState(false);
  const [currentOptions, setCurrentOptions] = useState([]);

  const handleOptionSelect = opt => {
    if (type === 'multi') {
      setTerm('');
      setShowOptions(false);
      onChange([...initialValue, opt]);
    } else {
      setTerm(opt.label);
      setShowOptions(false);
      onChange(opt);
    }
  };

  useEffect(() => {
    if (type !== 'multi' && initialValue && initialValue !== '') {
      setTerm(initialValue.label ?? '');
      setShowOptions(false);
    } else {
      setTerm('');
    }
  }, [initialValue, type]);

  useEffect(() => {
    if (options && onChange) {
      setCurrentOptions([...options]);
      if (options.length === 1) {
        setTerm(options[0].label);
        setShowOptions(false);
        onChange(options[0]);
      }
    }
  }, [options]);

  useEffect(() => {
    if (!initialValue && options.length > 0) {
      setCurrentOptions([...options]);
    }
  }, [initialValue, options]);

  const showOptionsAndFilter = useCallback(
    args => {
      if (minCharSearchLength) {
        String(args).length >= minCharSearchLength ? setShowOptions(true) : setShowOptions(false);
      } else {
        setShowOptions(true);
      }
      setCurrentOptions(filterOnChange(args[0], options, disableFuzzyMatch, fuseConfigKeys));
    },
    [minCharSearchLength, options, disableFuzzyMatch, fuseConfigKeys],
  );

  const handleOptionsFilter = useMemo(() => {
    return debounce(showOptionsAndFilter, 500);
  }, [showOptionsAndFilter]);

  const handleInputChange = e => {
    setTerm(e.target.value);
    if (
      initialValue &&
      initialValue.label &&
      shouldDisableSubmit(initialValue.label, e.target.value)
    ) {
      if (type !== 'multi') {
        onChange(null);
        handleOptionsFilter('');
      }
    } else {
      if (getTerm) getTerm(e.target.value);
      handleOptionsFilter(e.target.value);
    }
  };

  const handleMouseDownOnOption = e => e.preventDefault();

  const handleFocus = () => {
    if (!minCharSearchLength) {
      setShowOptions(true);
    }
    if (term && term.length) handleOptionsFilter(term);
    setFocussed(true);
  };

  const handleBlur = () => {
    if (type !== 'multi' && initialValue) setTerm(initialValue.label ?? '');
    else {
    }
    setShowOptions(false);
    setFocussed(false);
  };

  const handleOnCreate = () => {
    const data = {
      value: term,
      label: term,
    };
    onChange(data);
  };

  const openDropdown = e => {
    if (disabled) return;
    e.stopPropagation();
    if (showOptions) {
      setShowOptions(false);
    } else {
      if (term && term.length) handleOptionsFilter(term);
      else setShowOptions(true);
    }
  };

  return (
    <div className="custom_select_container">
      <div
        className={`custom_select_input_container ${focussed ? 'focussed' : ''} ${
          disabled ? 'disabled' : ''
        }`}
      >
        <input
          data-testid="custom_select_input"
          aria-label={label}
          className={`custom_select_input ${displayError ? 'show-error' : ''}`}
          value={term}
          type={'text'}
          placeholder={options.length ? placeholder : noOptionsMessage}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoFocus={autoFocus}
          disabled={disabled}
        />
        <div className="select_controls">
          <div className="icon_container" onClick={openDropdown}>
            {!showOptions ? <IconChevronDown /> : <IconChevronUp />}
          </div>
        </div>
      </div>
      {showOptions && (
        <ul className="custom_select_options">
          {currentOptions.length > 0 &&
            currentOptions.map((opt, i) => (
              <li
                key={i}
                value={opt.value}
                tabIndex={1}
                onMouseDown={handleMouseDownOnOption}
                onClick={() => handleOptionSelect(opt)}
              >
                {opt.label}
              </li>
            ))}
          {currentOptions.length < 1 && type !== 'creatable' && (
            <div className="custom_select_no_options">{noOptionsMessage}</div>
          )}
          {currentOptions.length < 1 && type === 'creatable' && (
            <p
              onMouseDown={handleMouseDownOnOption}
              onClick={handleOnCreate}
              className="custom_select_create_option"
            >
              {term}
            </p>
          )}
        </ul>
      )}
    </div>
  );
};

CustomSelect.propTypes = {
  noOptionsMessage: PropTypes.string,
  initialValue: PropTypes.oneOfType([
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    ),
  ]),
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    }),
  ).isRequired,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  disableFuzzyMatch: PropTypes.bool,
  fuseConfigKeys: PropTypes.arrayOf(PropTypes.string),
  getTerm: PropTypes.func,
  minCharSearchLength: PropTypes.number,
  label: PropTypes.string,
  displayError: PropTypes.bool,
};

CustomSelect.defaultProps = {
  noOptionsMessage: '',
  initialValue: null,
  onChange: () => {},
  type: '',
  placeholder: 'Search',
  autoFocus: true,
  disabled: false,
  disableFuzzyMatch: false,
  fuseConfigKeys: ['label'],
  getTerm: () => {},
  minCharSearchLength: null,
  label: '',
  displayError: false,
};

export default CustomSelect;
